/**
 * Lernstand: welche Karten es gibt, wie sie stehen, wann sie wieder dran sind.
 *
 * Alles liegt im Browser (localStorage), nichts geht ins Netz. Das ist zugleich
 * Produktversprechen (kein Konto, keine Erhebung) und die einfachste Lösung.
 */
import { allCardIds, binnenCardIds } from './quiz'
import { dayNumber, dueCards, newCard, review, successRate, type Card, type Grade } from './srs'
import { binnenSignale, binnenStates, buoys, lightStates, signals } from './data'
import { binschstro } from './sources'
import type { Bereich } from './settings'
import type { SignalData } from './schema'

/** Namen der Signalgruppen im Fortschritt, gleich benannt wie auf dem Signalbildschirm. */
const SIGNAL_GROUPS: Record<SignalData['context'], string> = {
  manoeuvre: 'Manöver',
  warning: 'Warnung',
  'narrow-channel': 'enges Fahrwasser',
  'restricted-visibility': 'Unsichtigkeit',
  identity: 'Kennung',
  distress: 'Notsignale',
}

/** Die Kategorien der Seezeichen, benannt wie auf dem Seezeichenschirm. */
const BUOY_GRUPPEN: Record<string, { en: string; de: string }> = {
  lateral: { en: 'Lateral marks', de: 'Laterale Zeichen' },
  'preferred-channel': { en: 'Preferred channel', de: 'Bevorzugtes Fahrwasser' },
  cardinal: { en: 'Cardinal marks', de: 'Kardinalzeichen' },
  'isolated-danger': { en: 'Isolated danger', de: 'Einzelgefahr' },
  'safe-water': { en: 'Safe water', de: 'Sicheres Fahrwasser' },
  special: { en: 'Special marks', de: 'Sonderzeichen' },
  mooring: { en: 'Mooring', de: 'Festmachen' },
  'emergency-wreck': { en: 'Emergency wreck', de: 'Notfallwrack' },
}

const KEY = 'colregs3d.progress.v1'

export interface Progress {
  cards: Record<string, Card>
  /** Saat für die Fragenfolge, damit ein Durchgang reproduzierbar bleibt. */
  seed: number
}

export function emptyProgress(seed = Math.floor(Math.random() * 1_000_000)): Progress {
  return { cards: {}, seed }
}

export function loadProgress(storage: Storage | undefined = safeStorage()): Progress {
  if (!storage) return emptyProgress()
  try {
    const raw = storage.getItem(KEY)
    if (!raw) return emptyProgress()
    const parsed = JSON.parse(raw) as Progress
    if (!parsed || typeof parsed !== 'object' || !parsed.cards) return emptyProgress()
    return parsed
  } catch {
    return emptyProgress()
  }
}

export function saveProgress(progress: Progress, storage: Storage | undefined = safeStorage()): void {
  try {
    storage?.setItem(KEY, JSON.stringify(progress))
  } catch {
    // Kein Speicher, kein Drama: der Lernstand gilt dann nur für diese Sitzung.
  }
}

/** Lernstand löschen. Die Einstellungen bleiben, nur das Gelernte geht. */
export function clearProgress(storage: Storage | undefined = safeStorage()): void {
  try {
    storage?.removeItem(KEY)
  } catch {
    // Nichts zu löschen, wenn es keinen Speicher gibt.
  }
}

function safeStorage(): Storage | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage
  } catch {
    return undefined
  }
}

export function cardOf(progress: Progress, id: string, today = dayNumber()): Card {
  return progress.cards[id] ?? newCard(id, today)
}

export function recordAnswer(progress: Progress, id: string, grade: Grade, today = dayNumber()): Progress {
  const card = review(cardOf(progress, id, today), grade, today)
  return { ...progress, cards: { ...progress.cards, [id]: card } }
}

/**
 * Die nächste Karte: was fällig ist, kommt zuerst, und darunter die schwächste.
 * Ist nichts fällig, kommt neuer Stoff. Das ist die übliche Reihenfolge und die
 * einzige, die sich in einem Satz erklären lässt.
 */
export function nextCardId(progress: Progress, today = dayNumber(), bereich: Bereich = 'see'): string | undefined {
  // Der Bereich entscheidet den Stoff: wer Binnen lernt, bekommt Binnenbilder, und wer
  // See lernt, sieht die Ordnung gar nicht. Der Lernstand hält beide auseinander, weil
  // die Kennungen getrennte Vorsilben tragen.
  const all = bereich === 'binnen' ? binnenCardIds() : allCardIds()
  const due = dueCards(
    all.filter((id) => progress.cards[id]).map((id) => progress.cards[id]!),
    today,
  )
  if (due.length > 0) return due[0]!.id
  return all.find((id) => !progress.cards[id])
}

export interface GroupProgress {
  key: string
  label: { en: string; de: string }
  cards: number
  seen: number
  rate: number
}

/**
 * Fortschritt je Gruppe, Schwachstellen zuerst.
 *
 * Die Gruppen folgen dem Bereich: auf See die Regeln der KVR, die Seezeichen und die
 * Schallsignale, im Binnenbereich die Paragraphen der Ordnung. Vorher waren sie fest aus
 * dem Seebestand gebaut, und wer im Binnenbereich lernte, sah nach einem ganzen
 * Prüfungsdurchgang weiter "0 von 70".
 */
export function progressByGroup(progress: Progress, bereich: Bereich = 'see'): GroupProgress[] {
  const groups = new Map<string, { label: { en: string; de: string }; ids: string[] }>()
  if (bereich === 'binnen') {
    for (const state of binnenStates) {
      const key = `binnen-${state.paragraph}`
      const label = { en: `§ ${state.paragraph}`, de: `§ ${state.paragraph}` }
      const group = groups.get(key) ?? { label, ids: [] }
      group.ids.push(`binnen:${state.id}`)
      groups.set(key, group)
    }
    // Die Schallzeichen nach den Abschnitten der Anlage 6: Allgemeine, Begegnung,
    // Überholen und so fort. Ohne diese Gruppen kämen die Karten zwar im Quiz vor, im
    // Stand aber nie, und wer sie übt, sähe seinen Fortschritt nicht.
    const titel = new Map(binschstro.anlage6.abschnitte.map((a) => [a.buchstabe, a.titel]))
    for (const zeichen of binnenSignale) {
      const key = `binnen-signal-${zeichen.abschnitt}`
      const name = `Schallzeichen, ${titel.get(zeichen.abschnitt) ?? zeichen.abschnitt}`
      const group = groups.get(key) ?? { label: { en: name, de: name }, ids: [] }
      group.ids.push(`binnenSignal:${zeichen.id}`)
      groups.set(key, group)
    }
    return sortiere(groups, progress)
  }
  for (const state of lightStates) {
    const key = `rule-${state.rule}`
    const group = groups.get(key) ?? { label: { en: `Rule ${state.rule}`, de: `Regel ${state.rule}` }, ids: [] }
    group.ids.push(`state:${state.id}`)
    groups.set(key, group)
  }
  for (const signal of signals) {
    const key = `signal-${signal.context}`
    const group = groups.get(key) ?? {
      label: { en: `Sound signals, ${signal.context}`, de: `Schallsignale, ${SIGNAL_GROUPS[signal.context]}` },
      ids: [],
    }
    group.ids.push(`signal:${signal.id}`)
    groups.set(key, group)
  }
  for (const buoy of buoys) {
    const key = `buoy-${buoy.category}`
    // Die Kategorien standen hier als Rohdaten ("lateral", "preferred-channel"), mitten
    // in einer deutschen Oberfläche. Sie heißen jetzt, wie sie auf dem Seezeichenschirm
    // heißen, und die Übersetzung liegt an einer Stelle.
    const group = groups.get(key) ?? { label: BUOY_GRUPPEN[buoy.category] ?? { en: buoy.category, de: buoy.category }, ids: [] }
    group.ids.push(`buoy:${buoy.id}`)
    groups.set(key, group)
  }
  return sortiere(groups, progress)
}

function sortiere(
  groups: Map<string, { label: { en: string; de: string }; ids: string[] }>,
  progress: Progress,
): GroupProgress[] {
  return [...groups.entries()]
    .map(([key, group]) => {
      const cards = group.ids.map((id) => progress.cards[id]).filter(Boolean) as Card[]
      const attempts = cards.reduce((n, c) => n + c.attempts, 0)
      const correct = cards.reduce((n, c) => n + c.correct, 0)
      return {
        key,
        label: group.label,
        cards: group.ids.length,
        seen: cards.length,
        rate: attempts === 0 ? 0 : correct / attempts,
      }
    })
    .sort((a, b) => (a.seen === 0 ? 1 : 0) - (b.seen === 0 ? 1 : 0) || a.rate - b.rate)
}

export { successRate }

/**
 * Die drei Körbe und der Fehlerkorb.
 *
 * Der Fortschritt zeigte bisher Balken je Regelgruppe: "Regel 23: 0/4". Das beantwortet
 * weder "wie gut stehe ich" noch "was mache ich jetzt", und im Binnenbereich stand dort
 * gar nichts, weil die Gruppen aus dem Seebestand gebaut waren.
 *
 * Drei Körbe beantworten beides in einem Zug, und jeder ist ein Knopf: was noch nie dran
 * war, was noch nicht sitzt, was sitzt. Dazu der Fehlerkorb mit dem, was zuletzt falsch
 * beantwortet wurde. So sieht man seinen Stand und hat zugleich die nächste Handlung.
 */
export type KorbId = 'neu' | 'wackelt' | 'sitzt' | 'fehler'

export interface Korb {
  id: KorbId
  karten: string[]
}

/** Eine Karte sitzt, wenn sie dreimal hintereinander richtig war. */
const SITZT_AB = 3

export function koerbe(progress: Progress, bereich: Bereich = 'see'): Korb[] {
  const alle = bereich === 'binnen' ? binnenCardIds() : allCardIds()
  const neu: string[] = []
  const wackelt: string[] = []
  const sitzt: string[] = []
  const fehler: string[] = []
  for (const id of alle) {
    const karte = progress.cards[id]
    if (!karte || karte.attempts === 0) {
      neu.push(id)
      continue
    }
    if (karte.repetitions >= SITZT_AB) sitzt.push(id)
    else wackelt.push(id)
    // Zuletzt falsch: die letzte Antwort hat die Folge gerissen.
    if (karte.repetitions === 0) fehler.push(id)
  }
  return [
    { id: 'neu', karten: neu },
    { id: 'wackelt', karten: wackelt },
    { id: 'sitzt', karten: sitzt },
    { id: 'fehler', karten: fehler },
  ]
}

/**
 * Die nächste Karte aus einem bestimmten Korb, oder nichts, wenn er leer ist.
 * Damit wird ein Korb zum Knopf: antippen und genau diese Karten üben.
 */
export function naechsteAusKorb(progress: Progress, korb: KorbId, bereich: Bereich = 'see'): string | undefined {
  const treffer = koerbe(progress, bereich).find((k) => k.id === korb)
  return treffer?.karten[0]
}
